import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { useKeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';

import { messages } from './messages.tsx';

const styles = cssMap({
	hiddenTitle: {
		position: 'absolute',
		width: '0px',
		height: '0px',
		padding: '0',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		border: 'none',
	},
});

/**
 * SearchDialogAnnouncer
 *
 * An aria-live region that announces the number of available items
 * in the Quick Find dialog to screen readers.
 *
 * @param isLoading - When true, suppresses announcements to avoid reading
 *   out stale or partial result counts while new results are still loading.
 */
export const SearchDialogAnnouncer = ({ isLoading }: { isLoading: boolean }): React.JSX.Element => {
	const dialogAnnouncerRef = useRef<HTMLDivElement>(null);
	const { keyboardNavComponents } = useKeyboardHighlightProvider();
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (dialogAnnouncerRef.current && !isLoading) {
			// Clear then re-set in the next frame so screen readers treat it
			// as a new announcement, even when the text is identical.
			dialogAnnouncerRef.current.textContent = '';
			const rafId = requestAnimationFrame(() => {
				if (dialogAnnouncerRef.current && keyboardNavComponents?.length) {
					dialogAnnouncerRef.current.textContent = formatMessage(messages.availableOptions, {
						count: keyboardNavComponents.length,
					});
				}
			});
			return () => cancelAnimationFrame(rafId);
		}
	}, [keyboardNavComponents?.length, isLoading, formatMessage]);

	return (
		<Box xcss={styles.hiddenTitle} aria-live="polite" aria-atomic={true} ref={dialogAnnouncerRef} />
	);
};
